// GRAVY-10073 / GRAVY-10101: shared helper for the staffing wizards'
// "Hours This Week" calculation. Returns the boundaries of the payroll
// workweek (US/Eastern, Monday 04:00 → next Monday 03:59:59) so the
// frontend's per-row at-risk highlighting agrees with the backend's
// `Workers::Overtime::WeeklyHoursService` / `ProjectionService` bucketing
// (which anchor on the same Eastern Monday-04:00 boundary via
// `TimeRange.payroll_week_start_*`). Bucketing in UTC instead under-counted
// the local week on EDT Sunday evenings (a Sun 8 PM ET shift fell into the
// next UTC week), so the over-40 warning failed to fire.

export type WeekRange = { startMs: number; endMs: number };

const TZ = 'America/New_York';
const FOUR_HOURS_MS = 4 * 3_600_000;

const EASTERN_DTF = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

type WallParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

// Eastern wall-clock components for a given instant.
const easternParts = (instantMs: number): WallParts => {
  const map: Record<string, string> = {};
  for (const p of EASTERN_DTF.formatToParts(new Date(instantMs))) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  let hour = parseInt(map.hour, 10);
  if (hour === 24) hour = 0; // some engines emit "24" for midnight
  return {
    year: +map.year,
    month: +map.month,
    day: +map.day,
    hour,
    minute: +map.minute,
    second: +map.second,
  };
};

// How far ahead of UTC the Eastern zone is at this instant, in ms
// (negative: EDT = −4h, EST = −5h).
const easternOffsetMs = (instantMs: number): number => {
  const p = easternParts(instantMs);
  const wallAsUtc = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
  );
  return wallAsUtc - instantMs;
};

// Convert an Eastern wall-clock time to the matching UTC instant. Uses the
// single-correction technique (with a second pass for the DST transition
// edge): guess the offset by treating the wall time as UTC, then refine.
const easternWallToUtcMs = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute = 0,
  second = 0,
): number => {
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  const offset1 = easternOffsetMs(asUtc);
  let utcMs = asUtc - offset1;
  const offset2 = easternOffsetMs(utcMs);
  if (offset2 !== offset1) utcMs = asUtc - offset2;
  return utcMs;
};

// The payroll-week Monday (as a UTC-encoded calendar date — read it via the
// getUTC* accessors) for the payroll week that contains `instantMs`. Treats
// the Eastern wall clock as a UTC-encoded value for pure calendar math, shifts
// back 4h so the payroll day rolls over at 04:00 Eastern, then snaps to that
// week's Monday.
const easternPayrollMonday = (instantMs: number): Date => {
  const p = easternParts(instantMs);
  const wallAsUtc = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
  );
  const shifted = new Date(wallAsUtc - FOUR_HOURS_MS);
  const daysFromMonday = (shifted.getUTCDay() + 6) % 7;
  return new Date(
    Date.UTC(
      shifted.getUTCFullYear(),
      shifted.getUTCMonth(),
      shifted.getUTCDate() - daysFromMonday,
    ),
  );
};

// Eastern-Monday payroll-week key (`YYYY-MM-DD`) for the payroll week that
// contains `instantMs`. Matches graphy's `TimeRange.payroll_week_start_date`
// (a Ruby Date serialized as `"YYYY-MM-DD"`), so the frontend's per-week
// selected hours join 1:1 against the backend's per-week existing hours
// (`hoursThisWeekByWeek`).
export const easternPayrollWeekKey = (instantMs: number): string => {
  const monday = easternPayrollMonday(instantMs);
  const y = monday.getUTCFullYear();
  const m = String(monday.getUTCMonth() + 1).padStart(2, '0');
  const d = String(monday.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const currentWeekRangeEastern = (now: Date = new Date()): WeekRange => {
  const monday = easternPayrollMonday(now.getTime());
  const startMs = easternWallToUtcMs(
    monday.getUTCFullYear(),
    monday.getUTCMonth() + 1,
    monday.getUTCDate(),
    4,
  );
  const nextMonday = new Date(
    Date.UTC(
      monday.getUTCFullYear(),
      monday.getUTCMonth(),
      monday.getUTCDate() + 7,
    ),
  );
  const endMs = easternWallToUtcMs(
    nextMonday.getUTCFullYear(),
    nextMonday.getUTCMonth() + 1,
    nextMonday.getUTCDate(),
    4,
  );
  return { startMs, endMs };
};

// Buckets `shifts` into payroll workweeks (Eastern, Monday 04:00) by each
// shift's own start instant, then returns the largest single-week net total.
// Used by the staffing wizards to feed `HoursThisWeekCell.selectedShiftHours`
// so the per-row at-risk red reflects the order's TARGET payroll week(s) —
// the same week the backend anchors `hoursThisWeek` to — rather than today's
// calendar week. Mirrors graphy's `Workers::Search::QueryService
// .compute_hours_this_week`, which reports the MAX hours across the order's
// impacted weeks. (A now-based "current week" filter left future-week
// overtime risk visually neutral: a clean future-week order that pushes the
// worker over 40 in the target week never turned the row red.)
//
// GRAVY-10073 break-aware: each shift may carry `mandatory_break_minutes`
// — the helper subtracts the value from gross so the per-row at-risk red
// matches the backend's net-of-break projection. Missing/0 break preserves
// the gross behavior. `Math.max(..., 0)` clamps the data-corruption case
// where break > gross.
export const maxWeeklyShiftHours = (
  shifts: Array<{
    start_at: string;
    end_at: string;
    mandatory_break_minutes?: number;
  }>,
): number => {
  const byWeek = weeklyShiftHoursByWeek(shifts);
  const max = Object.values(byWeek).reduce(
    (acc, hours) => (hours > acc ? hours : acc),
    0,
  );
  return Number(max.toFixed(2));
};

// Buckets `shifts` into payroll workweeks (Eastern, Monday 04:00) by each
// shift's own start instant and returns the net hours per week, keyed by the
// Eastern-Monday `YYYY-MM-DD` payroll-week key. Same per-week net-of-break
// accounting as `maxWeeklyShiftHours`, but keeps every week so the per-row
// "Hours This Week" cell can join these selected hours against the backend's
// per-week existing hours (`hoursThisWeekByWeek`) ONE WEEK AT A TIME — the max
// of each side could otherwise fall in DIFFERENT weeks and tip a row red even
// though no single payroll week actually exceeds 40 (the cross-week false
// positive, GRAVY row-indicator fix). The keys match graphy's
// `payroll_week_start_date` so both sides line up exactly.
export const weeklyShiftHoursByWeek = (
  shifts: Array<{
    start_at: string;
    end_at: string;
    mandatory_break_minutes?: number;
  }>,
): Record<string, number> => {
  const byWeek: Record<string, number> = {};
  for (const s of shifts) {
    const startMs = new Date(s.start_at).getTime();
    const endMs = new Date(s.end_at).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;
    const breakHours = (s.mandatory_break_minutes ?? 0) / 60;
    const netHours = Math.max((endMs - startMs) / 3_600_000 - breakHours, 0);
    if (netHours === 0) continue;
    const key = easternPayrollWeekKey(startMs);
    byWeek[key] = (byWeek[key] ?? 0) + netHours;
  }
  for (const key of Object.keys(byWeek)) {
    byWeek[key] = Number(byWeek[key].toFixed(2));
  }
  return byWeek;
};
